import { useCallback, useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import type { Gig } from '@stage/contracts';
import { ApiError } from '../api/client.js';
import { useAdminClient } from '../hooks/useAdminClient.js';
import { useAuth } from '../auth/AuthContext.js';
import { lifecycle, statusLabel } from '../lib/lifecycle.js';

/**
 * Control Center overview (§23.2): active/upcoming gigs with current live state and quick access to
 * the editor and Performance Console.
 *
 * Artist selection: this milestone consumes only `GET /v1/artists/{artistId}/gigs`, so the artist is
 * chosen via `?artistId=` (defaulting to `VITE_ARTIST_ID`). A full workspace/artist picker arrives
 * with the workspace endpoints.
 */
function defaultArtistId(): string {
  const meta = import.meta as unknown as { env?: Record<string, string | undefined> };
  return meta.env?.['VITE_ARTIST_ID'] ?? '';
}

export function OverviewPage() {
  const client = useAdminClient();
  const { user, logout } = useAuth();
  const [params, setParams] = useSearchParams();
  const artistId = params.get('artistId') ?? defaultArtistId();
  const [draft, setDraft] = useState(artistId);
  const [gigs, setGigs] = useState<Gig[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(
    async (id: string) => {
      if (!id) {
        setGigs(null);
        return;
      }
      setLoading(true);
      setError(null);
      try {
        setGigs(await client.listGigs(id));
      } catch (err) {
        setGigs(null);
        setError(err instanceof ApiError ? err.message : 'Could not load gigs.');
      } finally {
        setLoading(false);
      }
    },
    [client],
  );

  useEffect(() => {
    void load(artistId);
  }, [artistId, load]);

  const onSelectArtist = (e: FormEvent) => {
    e.preventDefault();
    setParams(draft ? { artistId: draft } : {});
  };

  return (
    <main className="page page--wide">
      <header className="app-bar">
        <div>
          <p className="eyebrow">Control Center</p>
          <h1>Overview</h1>
        </div>
        <div className="app-bar__account">
          {user?.email && <span className="muted">{user.email}</span>}
          <button type="button" className="link" onClick={logout}>
            Sign out
          </button>
        </div>
      </header>

      <form className="artist-picker card" onSubmit={onSelectArtist}>
        <label>
          Artist ID
          <input
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="artist_…"
            spellCheck={false}
            autoCapitalize="none"
          />
        </label>
        <button type="submit">Load gigs</button>
        {artistId && (
          <Link
            className="button-link"
            to={`/app/artists/${encodeURIComponent(artistId)}/songs`}
          >
            Manage repertoire
          </Link>
        )}
        {artistId && (
          <Link
            className="button-link"
            to={`/app/artists/${encodeURIComponent(artistId)}/improv-profiles`}
          >
            Improv profiles
          </Link>
        )}
        {artistId && (
          <Link
            className="button-link"
            to={`/app/artists/${encodeURIComponent(artistId)}/rhythm-catalogs`}
          >
            Rhythm catalogs
          </Link>
        )}
        {artistId && (
          <Link
            className="button-link"
            to={`/app/artists/${encodeURIComponent(artistId)}/integrations`}
          >
            Integrations
          </Link>
        )}
        {artistId && (
          <Link
            className="button-link"
            to={`/app/artists/${encodeURIComponent(artistId)}/analytics`}
          >
            Artist analytics
          </Link>
        )}
      </form>

      {!artistId && <p className="muted">Enter an artist ID to see its gigs.</p>}
      {loading && <p aria-busy="true">Loading gigs…</p>}
      {error && <p className="error">{error}</p>}

      {gigs && gigs.length === 0 && <p className="muted">No gigs yet for this artist.</p>}

      {gigs && gigs.length > 0 && (
        <ul className="gig-list">
          {gigs.map((gig) => (
            <li key={gig.gigId} className="card gig-row">
              <div className="gig-row__main">
                <h2>{gig.name}</h2>
                <p className="status">
                  <span className={`dot status-dot--${gig.status}`} />
                  {statusLabel(gig.status)}
                  {gig.venue.publicLabel && <span className="muted"> · {gig.venue.publicLabel}</span>}
                </p>
              </div>
              <div className="gig-row__actions">
                <Link className="button-link" to={`/app/gigs/${gig.gigId}`}>
                  Edit
                </Link>
                <Link className="button-link" to={`/app/gigs/${gig.gigId}/analytics`}>
                  Analytics
                </Link>
                {gig.artistId && (
                  <Link className="button-link" to={`/app/gigs/${gig.gigId}/tipping`}>
                    Tipping
                  </Link>
                )}
                {lifecycle.isRunning(gig.status) || gig.status === 'published' ? (
                  <Link className="button-link button-link--primary" to={`/app/gigs/${gig.gigId}/console`}>
                    Console
                  </Link>
                ) : (
                  <Link className="button-link" to={`/app/gigs/${gig.gigId}/console`}>
                    Console
                  </Link>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </main>
  );
}
